import { Link } from "wouter";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import PageHero from "@/components/PageHero";
import { CONTACT } from "@/lib/constants";

export default function Integritetspolicy() {
  return (
    <Layout>
      <SEOHead
        title="Integritetspolicy och cookiepolicy | ASPAB"
        description="Läs om hur ASPAB hanterar dina personuppgifter, vilka cookies vi använder och dina rättigheter enligt GDPR."
        canonical="https://aspab.com/integritetspolicy"
        path="/integritetspolicy"
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Integritetspolicy", href: "/integritetspolicy" },
        ]}
      
        ogImage="/images/og-integritetspolicy_cbeec9b0.png"
      />

      <PageHero
        title="Integritetspolicy"
        subtitle="Så hanterar vi dina personuppgifter och cookies."
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Integritetspolicy", href: "/integritetspolicy" },
        ]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
            <div className="max-w-3xl mx-auto">
              <p className="text-gray-500 text-sm mb-10">
                Senast uppdaterad: 28 september 2026
              </p>

              <div className="space-y-12">

                {/* 1. Personuppgiftsansvarig */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    1. Personuppgiftsansvarig
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    ASPAB (org.nr {CONTACT.orgNr}) med adress {CONTACT.address}, {CONTACT.city} är
                    personuppgiftsansvarig för behandlingen av dina personuppgifter på denna webbplats.
                    Det innebär att vi ansvarar för att dina uppgifter hanteras på ett korrekt och
                    säkert sätt i enlighet med EU:s dataskyddsförordning (GDPR).
                  </p>
                  <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <p className="text-gray-700 font-medium text-sm">Kontaktuppgifter</p>
                    <div className="text-gray-600 text-sm mt-2 space-y-1">
                      <p>ASPAB</p>
                      <p>{CONTACT.address}, {CONTACT.city}</p>
                      <p>E-post: <a href={`mailto:${CONTACT.email}`} className="text-[#2563EB] hover:underline">{CONTACT.email}</a></p>
                      <p>Telefon: <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`} className="text-[#2563EB] hover:underline">{CONTACT.phone}</a></p>
                      <p>Org.nr: {CONTACT.orgNr}</p>
                    </div>
                  </div>
                </div>

                {/* 2. Vilka uppgifter samlar vi in */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    2. Vilka personuppgifter samlar vi in?
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    Vi samlar in personuppgifter som du frivilligt lämnar till oss, samt viss teknisk
                    information som samlas in automatiskt när du besöker vår webbplats.
                  </p>

                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-6">
                    Uppgifter du lämnar till oss
                  </h3>
                  <p className="text-gray-600 mt-2 leading-relaxed">
                    När du fyller i vårt kontaktformulär eller kontaktar oss via e-post eller telefon
                    kan vi samla in följande uppgifter:
                  </p>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Uppgift</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Syfte</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Rättslig grund</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">Namn</td>
                          <td className="px-4 py-3">Identifiera dig och personalisera kommunikationen</td>
                          <td className="px-4 py-3">Berättigat intresse</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">E-postadress</td>
                          <td className="px-4 py-3">Besvara din förfrågan och skicka offert</td>
                          <td className="px-4 py-3">Berättigat intresse</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">Telefonnummer</td>
                          <td className="px-4 py-3">Kontakta dig vid behov (frivilligt fält)</td>
                          <td className="px-4 py-3">Berättigat intresse</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">Företagsnamn</td>
                          <td className="px-4 py-3">Förstå din verksamhet för bättre rådgivning (frivilligt fält)</td>
                          <td className="px-4 py-3">Berättigat intresse</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3">Meddelande</td>
                          <td className="px-4 py-3">Förstå ditt behov och förbereda offert</td>
                          <td className="px-4 py-3">Berättigat intresse</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-6">
                    Uppgifter om hur du hittade till oss
                  </h3>
                  <p className="text-gray-600 mt-2 leading-relaxed">
                    När du skickar kontaktformuläret skickas även uppgifter om hur du kom till
                    webbplatsen, om sådana finns. Har du avvisat marknadsföringscookies skickas inga
                    sådana uppgifter. Syftet är att se vilka annonser och kanaler som leder
                    till förfrågningar, så att vi kan lägga vår marknadsföring där den gör nytta.
                    Rättslig grund är vårt berättigade intresse av att följa upp vår marknadsföring.
                  </p>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Uppgift</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Innehåll</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">Kampanjparametrar</td>
                          <td className="px-4 py-3">Källa, medium, kampanj, innehåll och sökord från länken du klickade på (utm-parametrar)</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">Klick-id från annonser</td>
                          <td className="px-4 py-3">Ett slumpmässigt id som Google Ads (gclid, gbraid, wbraid) eller Meta (fbclid) lägger till i länken när du klickar på en annons</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">Landningssida</td>
                          <td className="px-4 py-3">Adressen till sidan du först kom till, utan frågesträng eller formulärdata</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3">Första besöket</td>
                          <td className="px-4 py-3">Kampanjparametrar, landningssida och tidpunkt för ditt allra första besök</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-6">
                    Teknisk information
                  </h3>
                  <p className="text-gray-600 mt-2 leading-relaxed">
                    När du besöker vår webbplats samlas viss teknisk information in automatiskt, bland
                    annat din IP-adress (anonymiserad), webbläsartyp, operativsystem, besökta sidor
                    och tidpunkt för besöket. Denna information används för att förbättra webbplatsens
                    funktion och analysera besöksmönster.
                  </p>
                </div>

                {/* 3. Cookies */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    3. Cookies och liknande tekniker
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    En cookie är en liten textfil som lagras på din enhet (dator, surfplatta eller
                    mobiltelefon) när du besöker en webbplats. Cookies hjälper webbplatsen att komma
                    ihåg information om ditt besök, till exempel dina inställningar, vilket gör nästa
                    besök enklare och webbplatsen mer användbar.
                  </p>
                  <p className="text-gray-600 mt-3 leading-relaxed">
                    Vi använder cookies i enlighet med EU:s ePrivacy-direktiv och GDPR. Vid ditt
                    första besök på vår webbplats visas en cookie-banner där du kan välja "Tillåt cookies",
                    "Avvisa allt" eller under "Anpassa" välja vilka kategorier du godkänner, till exempel
                    analys och marknadsföring.
                  </p>

                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-8">
                    3.1 Nödvändiga cookies
                  </h3>
                  <p className="text-gray-600 mt-2 leading-relaxed">
                    Dessa cookies krävs för att webbplatsen ska fungera korrekt. De kan inte stängas
                    av i våra system. De sätts vanligtvis som svar på åtgärder du gör, till exempel
                    att ställa in sekretessinställningar eller fylla i formulär.
                  </p>
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Cookie</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Syfte</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Lagringstid</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Typ</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3 font-mono text-xs">semantiko_cmp_v1</td>
                          <td className="px-4 py-3">Sparar ditt val i cookie-bannern så att den inte visas igen</td>
                          <td className="px-4 py-3">Permanent (localStorage)</td>
                          <td className="px-4 py-3">Nödvändig</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 font-mono text-xs">session</td>
                          <td className="px-4 py-3">Hanterar din inloggningssession om du är registrerad användare</td>
                          <td className="px-4 py-3">Sessionsbaserad</td>
                          <td className="px-4 py-3">Nödvändig</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-8">
                    3.2 Analyscookies
                  </h3>
                  <p className="text-gray-600 mt-2 leading-relaxed">
                    Dessa cookies hjälper oss att förstå hur besökare interagerar med webbplatsen
                    genom att samla in och rapportera information anonymt. Informationen används för
                    att förbättra webbplatsens innehåll och användarupplevelse. Dessa cookies sätts
                    bara om du godkänner analyscookies i cookie-bannern.
                  </p>
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Cookie</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Syfte</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Lagringstid</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Typ</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr>
                          <td className="px-4 py-3 font-mono text-xs">Webbanalys</td>
                          <td className="px-4 py-3">Samlar in anonymiserad statistik om besöksmönster, sidvisningar och trafikkällor</td>
                          <td className="px-4 py-3">Upp till 12 månader</td>
                          <td className="px-4 py-3">Analys</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-8">
                    3.3 Mätning av marknadsföring
                  </h3>
                  <p className="text-gray-600 mt-2 leading-relaxed">
                    När du kommer till webbplatsen via en länk med kampanjparametrar eller klick-id
                    från en annons läser vi av dessa, tillsammans med landningssidan och tidpunkten för
                    ditt första besök. Uppgifterna sparas i din webbläsare bara om du godkänner
                    marknadsföringscookies i cookie-bannern. Innan du har gjort ett val finns de bara
                    kvar under det pågående besöket och sparas inte på din enhet. Avvisar du
                    marknadsföringscookies sparas
                    ingenting, redan sparade uppgifter raderas och inga spårningsuppgifter skickas med
                    formuläret.
                  </p>
                  <p className="text-gray-600 mt-3 leading-relaxed">
                    Uppgifterna skickas till oss först när du skickar kontaktformuläret (se avsnitt
                    2). Uppgifter från ett annonsklick som är äldre än 90 dagar skickas inte med.
                  </p>
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Cookie</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Syfte</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Lagringstid</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Typ</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr>
                          <td className="px-4 py-3 font-mono text-xs">aspab-attribution</td>
                          <td className="px-4 py-3">Sparar kampanjparametrar, klick-id och landningssida så att en förfrågan kan kopplas till rätt kampanj. Sätts bara om du godkänner marknadsföringscookies.</td>
                          <td className="px-4 py-3">Tills du rensar webbplatsdata (localStorage); klick-id används i högst 90 dagar</td>
                          <td className="px-4 py-3">Marknadsföring</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-8">
                    3.4 Tredjepartstjänster
                  </h3>
                  <p className="text-gray-600 mt-2 leading-relaxed">
                    Vi använder Google Maps på vår kontaktsida för att visa var vi finns. Google kan
                    sätta egna cookies när kartan laddas. Dessa cookies kontrolleras av Google och
                    omfattas av Googles egen integritetspolicy. Kartan laddas med attributet
                    "loading=lazy" för att minimera onödig datainsamling.
                  </p>
                  <p className="text-gray-600 mt-3 leading-relaxed">
                    Kontaktformuläret skyddas mot skräppost och automatiserade inskick med Google
                    reCAPTCHA. Tjänsten laddas bara på kontaktsidan. reCAPTCHA samlar in uppgifter om
                    din enhet och hur du använder sidan, till exempel IP-adress, webbläsare, skärmstorlek,
                    muspekarens rörelser och cookies från Google, och skickar dem till Google för att
                    bedöma om inskicket kommer från en människa. Rättslig grund är vårt berättigade
                    intresse av att skydda formuläret mot missbruk. Behandlingen omfattas av Googles{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-[#2563EB] hover:underline"
                    >
                      integritetspolicy
                    </a>{" "}
                    och{" "}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-[#2563EB] hover:underline"
                    >
                      användarvillkor
                    </a>
                    .
                  </p>

                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-8">
                    3.5 Hantera och radera cookies
                  </h3>
                  <p className="text-gray-600 mt-2 leading-relaxed">
                    Du kan när som helst ändra dina cookie-inställningar genom att rensa din
                    webbläsares cookies och besöka vår webbplats igen. Då visas cookie-bannern på
                    nytt och du kan göra ett nytt val. Du kan också hantera cookies direkt i din
                    webbläsares inställningar. Observera att om du blockerar nödvändiga cookies kan
                    vissa delar av webbplatsen sluta fungera som förväntat.
                  </p>
                  <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-5">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      <span className="font-semibold">Så raderar du cookies i din webbläsare:</span>{" "}
                      Gå till inställningar i din webbläsare (Chrome, Firefox, Safari, Edge) och sök
                      efter "Cookies" eller "Webbplatsdata". Där kan du radera alla cookies eller välja
                      att ta bort cookies från specifika webbplatser. Att radera webbplatsdata för
                      aspab.com tar även bort uppgifter som sparats i localStorage, till exempel
                      aspab-attribution.
                    </p>
                  </div>
                </div>

                {/* 4. Behandling av kontaktformulärdata */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    4. Behandling av kontaktformulärdata
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    När du skickar in en förfrågan via vårt kontaktformulär behandlas dina uppgifter
                    på följande sätt:
                  </p>
                  <div className="mt-4 space-y-4">
                    <div className="flex gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#2563EB] text-white flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">1</div>
                      <div>
                        <p className="text-gray-800 font-medium">Insamling och validering</p>
                        <p className="text-gray-600 text-sm mt-1">Dina uppgifter valideras för att säkerställa att obligatoriska fält (namn, e-post, meddelande) är korrekt ifyllda. Inskicket kontrolleras även med Google reCAPTCHA för att stoppa skräppost.</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#2563EB] text-white flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">2</div>
                      <div>
                        <p className="text-gray-800 font-medium">Lagring och notifiering</p>
                        <p className="text-gray-600 text-sm mt-1">Din förfrågan sparas i vår databas tillsammans med eventuella bifogade filer och uppgifter om hur du hittade till oss, och skickas via e-post till vår personal så att vi kan besvara den. Du får även en bekräftelse till din e-postadress.</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#2563EB] text-white flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">3</div>
                      <div>
                        <p className="text-gray-800 font-medium">Svar och uppföljning</p>
                        <p className="text-gray-600 text-sm mt-1">Vi kontaktar dig via e-post eller telefon, normalt inom 24 timmar under vardagar.</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    Vi skyddar kontaktformuläret mot missbruk med hastighetsbegränsning (rate
                    limiting) och Google reCAPTCHA. Din IP-adress används för att begränsa antalet
                    inskick och sparas tillsammans med din förfrågan så att vi kan upptäcka och
                    utreda missbruk.
                  </p>
                </div>

                {/* 5. Lagringstid */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    5. Hur länge sparar vi dina uppgifter?
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    Vi sparar dina personuppgifter så länge det är nödvändigt för det ändamål de
                    samlades in för. Nedan följer en översikt av våra lagringstider:
                  </p>
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Uppgiftstyp</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Lagringstid</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">Anledning</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">Kontaktformulärdata</td>
                          <td className="px-4 py-3">Upp till 12 månader</td>
                          <td className="px-4 py-3">Besvara förfrågan och eventuell uppföljning</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">Offertunderlag</td>
                          <td className="px-4 py-3">Upp till 24 månader</td>
                          <td className="px-4 py-3">Uppföljning av kundrelation</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">Bokföringsunderlag</td>
                          <td className="px-4 py-3">7 år</td>
                          <td className="px-4 py-3">Lagkrav enligt bokföringslagen</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">IP-adress (rate limiting)</td>
                          <td className="px-4 py-3">Max 1 timme</td>
                          <td className="px-4 py-3">Skydd mot missbruk av kontaktformuläret</td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-4 py-3">IP-adress och spårningsuppgifter sparade med förfrågan</td>
                          <td className="px-4 py-3">Samma som kontaktformulärdata</td>
                          <td className="px-4 py-3">Upptäcka missbruk och följa upp marknadsföring</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3">Spårningsuppgifter i din webbläsare</td>
                          <td className="px-4 py-3">Tills du rensar webbplatsdata; klick-id skickas med i högst 90 dagar</td>
                          <td className="px-4 py-3">Koppla en senare förfrågan till rätt kampanj</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 6. Delning av uppgifter */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    6. Delning av personuppgifter
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    Vi säljer aldrig dina personuppgifter till tredje part. Vi kan komma att dela
                    dina uppgifter med följande kategorier av mottagare, men enbart i den utsträckning
                    det är nödvändigt:
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-gray-800 font-medium text-sm">Tjänsteleverantörer</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Företag som tillhandahåller webbhotell, e-posttjänster och IT-support. Dessa
                        parter behandlar uppgifter enbart enligt våra instruktioner och har ingått
                        personuppgiftsbiträdesavtal med oss.
                      </p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-gray-800 font-medium text-sm">Google</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Google tar emot uppgifter från din enhet när reCAPTCHA och Google Maps laddas
                        på kontaktsidan. Google kan behandla uppgifterna i USA. Google LLC är
                        certifierat enligt EU–US Data Privacy Framework, som EU-kommissionen bedömer
                        ger en adekvat skyddsnivå.
                      </p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-gray-800 font-medium text-sm">Myndigheter</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Om vi har en rättslig skyldighet att lämna ut uppgifter, till exempel till
                        Skatteverket i samband med bokföring.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 7. Dina rättigheter */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    7. Dina rättigheter enligt GDPR
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    Som registrerad har du ett antal rättigheter enligt EU:s dataskyddsförordning
                    (GDPR). Du kan utöva dessa rättigheter genom att kontakta oss på{" "}
                    <a href={`mailto:${CONTACT.email}`} className="text-[#2563EB] hover:underline">{CONTACT.email}</a>.
                  </p>
                  <div className="mt-6 space-y-4">
                    <div className="border-l-2 border-[#2563EB] pl-4">
                      <p className="text-gray-800 font-medium">Rätt till tillgång</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Du har rätt att begära information om vilka personuppgifter vi behandlar om
                        dig och få en kopia av dessa uppgifter.
                      </p>
                    </div>
                    <div className="border-l-2 border-[#2563EB] pl-4">
                      <p className="text-gray-800 font-medium">Rätt till rättelse</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Om dina uppgifter är felaktiga eller ofullständiga har du rätt att begära att
                        de rättas eller kompletteras.
                      </p>
                    </div>
                    <div className="border-l-2 border-[#2563EB] pl-4">
                      <p className="text-gray-800 font-medium">Rätt till radering</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Du har rätt att begära att vi raderar dina personuppgifter, förutsatt att det
                        inte finns rättsliga hinder (till exempel bokföringsskyldighet).
                      </p>
                    </div>
                    <div className="border-l-2 border-[#2563EB] pl-4">
                      <p className="text-gray-800 font-medium">Rätt till begränsning</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Du har rätt att begära att vi begränsar behandlingen av dina uppgifter under
                        vissa omständigheter, till exempel om du bestrider uppgifternas korrekthet.
                      </p>
                    </div>
                    <div className="border-l-2 border-[#2563EB] pl-4">
                      <p className="text-gray-800 font-medium">Rätt till dataportabilitet</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Du har rätt att få ut dina personuppgifter i ett strukturerat, maskinläsbart
                        format och överföra dem till en annan personuppgiftsansvarig.
                      </p>
                    </div>
                    <div className="border-l-2 border-[#2563EB] pl-4">
                      <p className="text-gray-800 font-medium">Rätt att invända</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Du har rätt att invända mot behandling som grundar sig på berättigat intresse.
                        Vi kommer då att upphöra med behandlingen om vi inte kan visa tvingande
                        berättigade skäl.
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 mt-6 leading-relaxed">
                    Om du anser att vi behandlar dina personuppgifter i strid med GDPR har du rätt
                    att lämna in ett klagomål till{" "}
                    <a
                      href="https://www.imy.se"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-[#2563EB] hover:underline"
                    >
                      Integritetsskyddsmyndigheten (IMY)
                    </a>
                    , som är tillsynsmyndighet i Sverige.
                  </p>
                </div>

                {/* 8. Säkerhet */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    8. Säkerhetsåtgärder
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    Vi vidtar lämpliga tekniska och organisatoriska åtgärder för att skydda dina
                    personuppgifter mot obehörig åtkomst, förlust eller förstörelse. Bland annat
                    använder vi:
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                      <p className="text-gray-600">Krypterad dataöverföring via HTTPS (SSL/TLS) på hela webbplatsen</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                      <p className="text-gray-600">Skydd mot missbruk genom hastighetsbegränsning och Google reCAPTCHA på formulär</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                      <p className="text-gray-600">Validering av indata för att förhindra skadlig kod</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                      <p className="text-gray-600">Begränsad åtkomst till personuppgifter inom organisationen</p>
                    </div>
                  </div>
                </div>

                {/* 9. Ändringar */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    9. Ändringar av denna policy
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    Vi kan komma att uppdatera denna integritetspolicy vid behov, till exempel vid
                    förändringar i lagstiftning eller i våra tjänster. Den senaste versionen finns
                    alltid tillgänglig på denna sida. Vid väsentliga ändringar som påverkar hur vi
                    behandlar dina personuppgifter kommer vi att informera dig via webbplatsen.
                  </p>
                </div>

                {/* 10. Kontakt */}
                <div>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    10. Kontakta oss
                  </h2>
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    Har du frågor om denna integritetspolicy, vår hantering av personuppgifter
                    eller vill utöva dina rättigheter? Kontakta oss gärna:
                  </p>
                  <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <div className="text-gray-600 text-sm space-y-1">
                      <p className="font-medium text-gray-800">ASPAB</p>
                      <p>{CONTACT.address}, {CONTACT.city}</p>
                      <p>E-post: <a href={`mailto:${CONTACT.email}`} className="text-[#2563EB] hover:underline">{CONTACT.email}</a></p>
                      <p>Telefon: <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`} className="text-[#2563EB] hover:underline">{CONTACT.phone}</a></p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-12 pt-8 border-t border-gray-200 text-center">
                  <p className="text-gray-500 text-sm">
                    Vill du veta mer om hur vi arbetar?{" "}
                    <Link href="/om-oss" className="text-[#2563EB] hover:underline">
                      Läs om ASPAB
                    </Link>{" "}
                    eller{" "}
                    <Link href="/kontakt" className="text-[#2563EB] hover:underline">
                      kontakta oss direkt
                    </Link>
                    .
                  </p>
                </div>

              </div>
            </div>
        </div>
      </section>
    </Layout>
  );
}
